import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ChevronRight, CheckCircle2, XCircle } from "lucide-react";

export default function TakeTest() {
  const { assessmentId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/assessments/${assessmentId}/questions`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Failed to load test"));
  }, [assessmentId]);

  if (err) return <div className="p-10 text-sm text-[#C41E1E]">{err}</div>;
  if (!data) return <div className="p-10 text-sm text-[#666666]">Loading test…</div>;

  if (result) {
    const passed = result.result === "pass";
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F9] px-4">
        <div className="max-w-md w-full bc-card p-8 text-center" data-testid="test-result">
          <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${passed ? "bg-green-100 text-green-700" : "bg-red-100 text-[#C41E1E]"}`}>
            {passed ? <CheckCircle2 size={36} /> : <XCircle size={36} />}
          </div>
          <div className="bc-label mt-6">{passed ? "Competency confirmed" : "Gap identified"}</div>
          <h2 className="font-heading text-2xl font-bold mt-1">{passed ? "Passed" : "Did not pass"}</h2>
          <div className="mt-6 grid grid-cols-3 gap-4 text-center">
            <div><div className="bc-label">Score</div><div className="font-mono text-xl font-semibold mt-1">{result.score_percent}%</div></div>
            <div><div className="bc-label">Achieved</div><div className="font-mono text-xl font-semibold mt-1">{result.achieved_level}</div></div>
            <div><div className="bc-label">Required</div><div className="font-mono text-xl font-semibold mt-1">{result.required_level}</div></div>
          </div>
          <p className="text-sm text-[#666666] mt-6">
            {passed
              ? "Your competency for this skill has been confirmed and your card updated."
              : "A training need has been opened. Look out for an upcoming training session."}
          </p>
          <button
            data-testid="back-home"
            onClick={() => nav("/")}
            className="mt-6 w-full bg-[#0D0D0D] hover:bg-neutral-800 text-white rounded-sm py-3 text-sm font-medium"
          >
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  const total = data.questions.length;
  const q = data.questions[idx];
  const answered = Object.keys(answers).length;
  const allAnswered = answered === total;

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        answers: data.questions.map((q) => ({
          question_id: q.question_id,
          selected_option: answers[q.question_id] || "",
        })),
      };
      const res = await api.post(`/assessments/${assessmentId}/submit`, payload);
      setResult(res.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F9F9F9]" data-testid="take-test-page">
      {/* Top */}
      <header className="bg-[#0D0D0D] text-white px-4 md:px-10 py-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-400">{data.assessment.type} · {data.assessment.difficulty} · Required {data.assessment.required_level}</div>
            <h1 className="font-heading text-xl font-bold mt-1">{data.assessment.skill_name}</h1>
          </div>
          <div className="font-mono text-sm">{idx + 1} / {total}</div>
        </div>
        <div className="h-1 bg-neutral-800 mt-3 rounded-sm overflow-hidden">
          <div className="h-full bg-[#C41E1E] transition-all" style={{ width: `${((idx + 1) / total) * 100}%` }} />
        </div>
      </header>

      {/* Question */}
      <div className="flex-1 px-4 md:px-10 py-8 md:py-12 pb-32">
        <div className="max-w-2xl mx-auto">
          <div className="bc-label">Question {idx + 1}</div>
          <h2 className="font-heading text-xl md:text-2xl font-semibold leading-snug mt-2" data-testid="question-text">{q.question_text}</h2>
          <div className="mt-6 space-y-3">
            {q.options.map((opt, i) => {
              const letter = ["A", "B", "C", "D"][i];
              const selected = answers[q.question_id] === letter;
              return (
                <button
                  key={letter}
                  data-testid={`option-${letter}`}
                  onClick={() => setAnswers((prev) => ({ ...prev, [q.question_id]: letter }))}
                  className={`w-full text-left bc-card px-4 py-4 flex items-start gap-4 transition-colors ${
                    selected ? "border-[#C41E1E] bg-red-50/30" : "hover:border-[#0D0D0D]"
                  }`}
                >
                  <span className={`shrink-0 w-7 h-7 flex items-center justify-center font-mono font-semibold text-xs rounded-sm border ${
                    selected ? "bg-[#C41E1E] text-white border-[#C41E1E]" : "border-[#E5E5E5] text-[#666666]"
                  }`}>{letter}</span>
                  <span className="text-sm">{opt}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Sticky bottom action */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-[#E5E5E5] px-4 md:px-10 py-3 flex items-center justify-between">
        <div className="text-xs text-[#666666]">{answered}/{total} answered</div>
        <div className="flex items-center gap-2">
          {idx > 0 && (
            <button data-testid="prev-button" onClick={() => setIdx(Math.max(0, idx - 1))} className="text-sm px-4 py-2 border border-[#E5E5E5] hover:border-[#0D0D0D] rounded-sm">
              Previous
            </button>
          )}
          {idx < total - 1 ? (
            <button
              data-testid="next-button"
              onClick={() => setIdx(idx + 1)}
              disabled={!answers[q.question_id]}
              className="inline-flex items-center gap-1.5 text-sm px-4 py-2 bg-[#0D0D0D] hover:bg-neutral-800 text-white rounded-sm disabled:opacity-40"
            >
              Next <ChevronRight size={14} />
            </button>
          ) : (
            <button
              data-testid="submit-test-button"
              onClick={submit}
              disabled={!allAnswered || submitting}
              className="text-sm px-5 py-2 bg-[#C41E1E] hover:bg-[#A31818] text-white rounded-sm disabled:opacity-40 font-medium"
            >
              {submitting ? "Submitting…" : "Submit test"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
